/**
 * 搜索查询绩效API服务
 */

import axios from 'axios';
import type {
  SearchPerformancePeriod,
  AdvantageKeywordsResult,
  MarketOverview,
  OpportunityKeywordsResult,
  FunnelAnalysis,
  DeliveryAnalysis,
  MarketTrends,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2分钟超时
});

export interface AnalysisParams {
  period_ids?: number[];
  top_n?: number;
  search_query?: string;
  limit?: number;
}

export const searchPerformanceAPI = {
  /**
   * 上传CSV文件
   */
  upload: (skuId: number, file: File) => {
    const formData = new FormData();
    formData.append('sku', skuId.toString());
    formData.append('csv_file', file);

    return api.post<{
      period: SearchPerformancePeriod;
      validation_info: string[];
      warnings: string[];
    }>('/search-performance/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * 获取某个SKU的所有周期
   */
  getPeriods: (skuId: number) => {
    return api.get<SearchPerformancePeriod[]>(
      `/search-performance/sku/${skuId}/periods/`
    );
  },

  /**
   * 获取优势关键词分析
   */
  getAdvantageKeywords: (skuId: number, params?: AnalysisParams) => {
    return api.get<AdvantageKeywordsResult>(
      `/search-performance/sku/${skuId}/advantage-keywords/`,
      {
        params: {
          period_ids: params?.period_ids,
          top_n: params?.top_n || 10,
        },
        paramsSerializer: (params) => {
          // 处理数组参数
          const parts: string[] = [];
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${v}`));
            } else if (value !== undefined) {
              parts.push(`${key}=${value}`);
            }
          });
          return parts.join('&');
        },
      }
    );
  },

  /**
   * 获取大盘概况分析
   */
  getMarketOverview: (skuId: number, params?: AnalysisParams) => {
    return api.get<MarketOverview>(
      `/search-performance/sku/${skuId}/market-overview/`,
      {
        params: {
          period_ids: params?.period_ids,
        },
        paramsSerializer: (params) => {
          const parts: string[] = [];
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${v}`));
            } else if (value !== undefined) {
              parts.push(`${key}=${value}`);
            }
          });
          return parts.join('&');
        },
      }
    );
  },

  /**
   * 获取机会词挖掘分析
   */
  getOpportunityKeywords: (skuId: number, params?: AnalysisParams) => {
    return api.get<OpportunityKeywordsResult>(
      `/search-performance/sku/${skuId}/opportunity-keywords/`,
      {
        params: {
          period_ids: params?.period_ids,
          top_n: params?.top_n || 20,
        },
        paramsSerializer: (params) => {
          const parts: string[] = [];
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${v}`));
            } else if (value !== undefined) {
              parts.push(`${key}=${value}`);
            }
          });
          return parts.join('&');
        },
      }
    );
  },

  /**
   * 获取转化漏斗对比分析
   */
  getFunnelAnalysis: (skuId: number, params?: AnalysisParams) => {
    return api.get<FunnelAnalysis>(
      `/search-performance/sku/${skuId}/funnel-analysis/`,
      {
        params: {
          period_ids: params?.period_ids,
          search_query: params?.search_query,
        },
        paramsSerializer: (params) => {
          const parts: string[] = [];
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${v}`));
            } else if (value !== undefined) {
              parts.push(`${key}=${value}`);
            }
          });
          return parts.join('&');
        },
      }
    );
  },

  /**
   * 获取配送速度影响分析
   */
  getDeliveryAnalysis: (skuId: number, params?: AnalysisParams) => {
    return api.get<DeliveryAnalysis>(
      `/search-performance/sku/${skuId}/delivery-analysis/`,
      {
        params: {
          period_ids: params?.period_ids,
        },
        paramsSerializer: (params) => {
          const parts: string[] = [];
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${v}`));
            } else if (value !== undefined) {
              parts.push(`${key}=${value}`);
            }
          });
          return parts.join('&');
        },
      }
    );
  },

  /**
   * 获取市场趋势分析
   */
  getMarketTrends: (skuId: number, params?: AnalysisParams) => {
    return api.get<MarketTrends>(
      `/search-performance/sku/${skuId}/market-trends/`,
      {
        params: {
          limit: params?.limit || 8,
        },
      }
    );
  },

  /**
   * 获取关键词多周期透视表
   */
  getKeywordPivot: (skuId: number) => {
    return api.get<any>(
      `/search-performance/sku/${skuId}/keyword-pivot/`
    );
  },

  /**
   * 删除周期
   */
  deletePeriod: (periodId: number) => {
    return api.delete(`/search-performance/${periodId}/`);
  },
};

export default searchPerformanceAPI;

